import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncConfirmationDialog, NoddiButton, NoddiCard, NoddiInfoBanner, NoddiInput } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useState } from 'react';
import { useAuthContext } from '../../contexts/useAuthContext';
import { buildRegistrationPayload, createPasskey, isWebAuthnSupported } from '../Login/passkeyUtils';

interface PasskeyManagementProps {
  translations?: {
    passkeys: string;
    addPasskey: string;
    passkeyName: string;
    createPasskey: string;
    deletePasskey: string;
    noPasskeys: string;
    passkeyNotSupported: string;
    createdAt: string;
    friendlyName: string;
  };
}

export const PasskeyManagement = ({ translations }: PasskeyManagementProps) => {
  const { userData } = useAuthContext();
  const [friendlyName, setFriendlyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const texts = translations ?? {
    passkeys: 'Passkeys',
    addPasskey: 'Add passkey',
    passkeyName: 'Passkey name',
    createPasskey: 'Create passkey',
    deletePasskey: 'Delete',
    noPasskeys: 'No passkeys registered',
    passkeyNotSupported: 'Passkeys are not supported in your browser',
    createdAt: 'Created',
    friendlyName: 'Name'
  };

  const userId = userData?.user.id;

  const {
    data: passkeysData,
    error: passkeysError,
    isPending: isPasskeysPending
  } = noddiAsync.useGet({
    type: URLKeys.getPasskeys,
    input: undefined,
    queryConfig: {
      enabled: !!userId
    }
  });

  const { mutateAsync: createRegistrationChallenge } = noddiAsync.usePost({
    type: URLKeys.postPasskeyRegistrationChallenge
  });

  const { mutateAsync: registerPasskey } = noddiAsync.usePost({
    type: URLKeys.postPasskeyRegistrationVerify,
    queryConfig: {
      onSuccess: () => {
        setIsCreating(false);
        setFriendlyName('');
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getPasskeys });
      }
    }
  });

  const { mutateAsync: deletePasskey, isPending: isDeletingPasskey } = noddiAsync.useDelete({
    type: URLKeys.deletePasskey,
    queryConfig: {
      onSuccess: () => {
        setError(null);
        invalidateQuery({ urlKey: URLKeys.getPasskeys });
      }
    }
  });

  const handleCreatePasskey = async () => {
    if (!userId || !friendlyName.trim() || !isWebAuthnSupported()) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const challengeResponse = await createRegistrationChallenge({
        friendlyName: friendlyName.trim()
      });

      const { challengeSid, options } = challengeResponse.data;
      const credential = await createPasskey(options);
      const payload = buildRegistrationPayload(challengeSid, credential);

      await registerPasskey(payload);
    } catch (err) {
      setIsCreating(false);
      setError(err instanceof Error ? err.message : 'Failed to create passkey');
    }
  };

  if (!userId) {
    return null;
  }

  const passkeys = passkeysData?.passkeys ?? [];
  const isSupported = isWebAuthnSupported();

  return (
    <NoddiCard title={texts.passkeys} description='Manage your passkeys for secure authentication.'>
      <div className='flex flex-col gap-4'>
        {error && <NoddiInfoBanner variant='error' texts={[{ value: error }]} />}

        {!isSupported && <NoddiInfoBanner variant='warning' texts={[{ value: texts.passkeyNotSupported }]} />}

        {isSupported && (
          <div className='flex flex-col gap-2 rounded-lg border p-4'>
            <h3 className='text-lg font-semibold'>{texts.addPasskey}</h3>
            <div className='flex flex-col gap-2 md:flex-row'>
              <div className='flex-1'>
                <NoddiInput
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                  placeholder={texts.passkeyName}
                  label={texts.passkeyName}
                  disabled={isCreating}
                />
              </div>
              <div className='flex items-end'>
                <NoddiButton
                  // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleCreatePasskey(); React discards the returned promise, so a rejection is unhandled unless the body catches
                  onClick={handleCreatePasskey}
                  loading={isCreating}
                  disabled={isCreating || !friendlyName.trim()}
                >
                  {texts.createPasskey}
                </NoddiButton>
              </div>
            </div>
          </div>
        )}

        <div className='flex flex-col gap-2'>
          <h3 className='text-lg font-semibold'>Registered passkeys</h3>
          {isPasskeysPending ? (
            <div className='text-muted-foreground'>Loading passkeys...</div>
          ) : passkeysError ? (
            <NoddiInfoBanner variant='error' texts={[{ value: 'Failed to load passkeys' }]} />
          ) : passkeys.length === 0 ? (
            <div className='text-muted-foreground'>{texts.noPasskeys}</div>
          ) : (
            <div className='flex flex-col gap-2'>
              {passkeys.map((passkey) => (
                <div key={passkey.credentialSid} className='flex items-center justify-between rounded-lg border p-3'>
                  <div className='flex flex-col'>
                    <span className='font-medium'>{passkey.friendlyName}</span>
                    <span className='text-sm text-muted-foreground'>
                      {texts.createdAt}: {noddiFormatDate(passkey.createdAt, NoddiDateFormats.DATE)}
                    </span>
                  </div>
                  <NoddiAsyncConfirmationDialog
                    title='Delete passkey'
                    description={`Are you sure you want to delete the passkey "${passkey.friendlyName}"?`}
                    trigger={
                      <NoddiButton variant='destructive' size='small'>
                        {texts.deletePasskey}
                      </NoddiButton>
                    }
                    confirmAction={async () => {
                      await deletePasskey({ factorSid: passkey.credentialSid });
                    }}
                    confirmActionLoading={isDeletingPasskey}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </NoddiCard>
  );
};
